import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextField,
  Link,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import "../../styles/inputBox.css";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import uploadFileIcon from "../../assets/images/uploadFileIcon.svg";
import { useNavigate, useParams } from "react-router-dom";
import { getRequest, multiPartPUTRequest } from "../../api/apiServices";
import { getUserProfile, updateUserProfile } from "../../api/apiList";
import Toast from "../../utils/Toast";
import Loading from "../../utils/Loading";
import { useAuth } from "../../hooks/useAuth";
import { roleBasedRedirection } from "../../utils/helper";

interface ProfileProps {}

const UserProfile: React.FC<ProfileProps> = ({}: ProfileProps) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const { updateProfile } = useAuth();

  // handle Form field
  const [profile, setProfile] = useState<File | string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  const [errors, setErrors] = useState({
    profile: "",
    name: "",
    email: "",
  });

  const formIsValid = () => {
    const newErrors = {
      profile: !profile ? "Logo is required" : "",
      email: !email?.trim() ? "Email is required" : "",
      name: !name?.trim() ? "Name is required" : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  //params
  const params = useParams();
  const navigate = useNavigate();

  const handleUpdatePassword = () => {
    // navigate(`/update-user-password/${params?.id}`)
    navigate(roleBasedRedirection(`/update-user-password/${params?.id}`));
  };

  const goBack = () => {
    navigate("/");
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    if (formIsValid()) {
      try {
        setLoading(true);

        // Create FormData for multipart request
        const formData = new FormData();
        formData.append("name", name);

        // Only append profile if it's a new File upload, not an existing URL string
        if (profile && typeof profile !== "string") {
          formData.append("profile", profile);
        }

        const response = await multiPartPUTRequest(
          updateUserProfile(params?.id),
          formData
        );

        const updateData = {
          name: response?.data?.data?.name,
          profile: response?.data?.data?.profile,
        };

        updateProfile(updateData);
        setLoading(false);
        Toast(response?.data?.statusMessage || "Profile updated successfully");
        navigate("/");
      } catch (error) {
        setLoading(false);
        console.error("Error updating profile:", error);
        Toast("Failed to update profile. Please try again.", "error");
      }
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      if (event.target.files && event.target.files.length > 0) {
        setProfile(event.target.files[0]);
        setErrors((prevErrors) => ({
          ...prevErrors,
          profile: "",
        }));
      }
    }
  };

  const handleClearImage = () => {
    setProfile(null);
  };

  const getUserData = async () => {
    setFetchLoading(true);
    const response = await getRequest(getUserProfile(params?.id));
    setProfile(response?.data?.data?.user?.profile);
    setEmail(response?.data?.data?.user?.email);
    setName(response?.data?.data?.user?.name);
    setFetchLoading(false);
  };

  useEffect(() => {
    getUserData();
  }, []);

  const handleGridClick = () => {
    document.getElementById("file-upload")?.click();
  };

  return fetchLoading ? (
    <Loading />
  ) : (
    <div>
      <Container
        maxWidth="md"
        style={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="784px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? "auto" : "auto"}
            style={{ padding: 30 }}
          >
            <Grid
              container
              direction="row"
              display="flex"
              alignItems="center"
              gap="10px"
            >
              <ArrowBackIcon onClick={goBack} style={{ cursor: "pointer" }} />
              <Typography
                className="profile-title"
                color="#273848"
                fontWeight={700}
                fontSize="20.94px"
              >
                Update Your Profile
              </Typography>
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Profile Picture <span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <Box
                sx={{
                  border: "2px dashed #D5D5D5",
                  borderRadius: "20px",
                  padding: profile ? 0 : isMobile ? "30px" : "48px",
                  cursor: "pointer",
                  position: "relative",
                  transition: "all 0.3s ease",
                  background: profile
                    ? "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
                    : "#ffffff",
                  "&:hover": {
                    borderColor: "#84E1CC",
                    borderStyle: "solid",
                    boxShadow: "0 8px 16px rgba(132, 225, 204, 0.2)",
                  },
                }}
                onClick={profile ? undefined : handleGridClick}
              >
                {profile ? (
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: 3,
                      minHeight: "200px",
                      "&:hover .delete-overlay": {
                        opacity: 1,
                      },
                    }}
                  >
                    <img
                      src={
                        typeof profile === "string"
                          ? profile
                          : URL.createObjectURL(profile)
                      }
                      alt="selected-image"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        borderRadius: "12px",
                        objectFit: "contain",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      }}
                    />

                    {/* Modern Delete Overlay */}
                    <Box
                      className="delete-overlay"
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearImage();
                        }}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(239, 68, 68, 0.4)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.1) rotate(90deg)",
                            boxShadow: "0 6px 16px rgba(239, 68, 68, 0.6)",
                          },
                        }}
                      >
                        <CloseIcon
                          sx={{
                            color: "#ffffff",
                            fontSize: 24,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Change Image Button */}
                    <Box
                      className="delete-overlay"
                      sx={{
                        position: "absolute",
                        bottom: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGridClick();
                        }}
                        sx={{
                          background:
                            "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
                          color: "#fff",
                          borderRadius: "12px",
                          padding: "10px 24px",
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: "0 4px 12px rgba(132, 225, 204, 0.4)",
                          "&:hover": {
                            boxShadow: "0 6px 16px rgba(132, 225, 204, 0.6)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        Change Image
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 2,
                    }}
                  >
                    <img src={uploadFileIcon} alt="uploadFile-Icon" />
                    <Grid my={1}></Grid>
                    <Typography align="center" color="#4E4E4E">
                      Drag a file or{" "}
                      <Link
                        color="#84E1CC"
                        sx={{
                          textDecorationColor: "#84E1CC",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        select
                      </Link>
                    </Typography>
                  </Box>
                )}
                <input
                  id="file-upload"
                  type="file"
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Box>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.profile}
              </InputLabel>
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Name<span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 11px", // Add padding here
                  },
                }}
                placeholder="Name"
                fullWidth
                className="input-box-custom"
                error={!!errors.name}
                helperText={errors.name}
                value={name}
                onChange={(e) => {
                  const value = e.target.value;
                  setName(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      name: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={2}></Grid>
            <Grid item>
              <Typography className="input-label-custom">
                Email<span style={{ color: "red" }}>*</span>
              </Typography>
              <Grid mb={1}></Grid>
              <TextField
                sx={{
                  // mt: 1,
                  "& .MuiOutlinedInput-input": {
                    padding: "10px 11px", // Add padding here
                  },
                }}
                disabled
                placeholder="Email"
                fullWidth
                className="input-box-custom"
                error={!!errors.email}
                helperText={errors.email}
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  if (value.trim() !== "") {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      email: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid my={4}></Grid>
            <Grid
              container
              direction="row"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item style={{ maxWidth: "fitContent" }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                  }}
                  onClick={handleUpdatePassword}
                >
                  Change your password
                </Button>
              </Grid>
              <Grid item style={{ maxWidth: "fitContent" }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    textTransform: "none",
                    letterSpacing: "0px",
                    position: "relative",
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  {/* {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Update profile" */}
                  {/* Keep width stable: show text but hide visually when loading */}
                  <span style={{ visibility: loading ? "hidden" : "visible" }}>
                    Update profile
                  </span>
                  {loading && (
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{
                        position: "absolute",
                        // top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  )}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default UserProfile;
